import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type DateValue, getLocalTimeZone } from "@internationalized/date";
import type { Metric } from "@unbox/api";
import { BarChartSquare02, LineChartUp02, Trash02, Type02 } from "@untitledui/icons";
import { GripVertical } from "lucide-react";
import type React from "react";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { RadioButton, RadioGroup } from "@/components/base/radio-buttons/radio-buttons";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { getMetricsArray, METRICS_MAP } from "@/constants/metrics";
import { ChartFormatIcon } from "@/pages/reports/graphics/ChartFormatIcon";
import { TableFormatIcon } from "@/pages/reports/graphics/TableFormatIcon";

export interface MetricsFormData {
  name: string;
  layout: "chart" | "table" | "bar";
  metrics: Metric[];
}

interface MetricsConfigurationFormProps {
  onSubmit: (data: MetricsFormData) => Promise<unknown> | unknown;
  initialData?: MetricsFormData;
  isEditMode?: boolean;
  isSubmitting?: boolean;
  autoCloseOnSubmit?: boolean;
  generateSuggestedName?: (params: {
    layout: "chart" | "table" | "bar";
    chartMetrics: (Metric | "")[];
    tableMetrics: (Metric | "")[];
    dateRange?: { start: DateValue; end: DateValue } | null;
  }) => string;
  nameLabel?: string;
  /** Label for the details section (e.g., "Report Details" or "Analytics View Details") */
  detailsSectionLabel?: string;
  /** Subtitle for the details section (e.g., "Name" or "Name and description") */
  detailsSectionSubtitle?: string;
  /** If true, form will reset when initialData changes (for modal open/close behavior) */
  resetOnInitialDataChange?: boolean;
  /** Callback when form validity changes */
  onValidityChange?: (isValid: boolean) => void;
  /** Optional slot to render content above the report name field (e.g., date range selector) */
  dateRangeSlot?: React.ReactNode;
  /** Date range for name generation */
  dateRange?: { start: DateValue; end: DateValue } | null;
  /** Optional filtered metrics array (e.g., to hide Ads metrics when user hasn't authed) */
  filteredMetrics?: Array<{ value: string; label: string; description: string }>;
}

export interface MetricsConfigurationFormHandle {
  submit: () => Promise<unknown>;
  isValid: boolean;
}

interface SortableTableMetricProps {
  id: string;
  metric: string;
  onRemove: () => void;
  onMetricChange: (newMetric: Metric) => void;
  availableMetrics: Array<{ value: string; label: string; description: string }>;
  selectedMetrics: string[];
}

const SortableTableMetric: React.FC<SortableTableMetricProps> = ({
  id,
  metric,
  onRemove,
  onMetricChange,
  availableMetrics,
  selectedMetrics,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const selectableMetrics = availableMetrics.filter((m) => m.value === metric || !selectedMetrics.includes(m.value));

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 min-w-0">
      <div {...attributes} {...listeners} className="cursor-move shrink-0">
        <GripVertical className="w-5 h-5 text-quaternary" />
      </div>
      <div className="flex-1 min-w-0">
        <Select
          selectedKey={metric}
          onSelectionChange={(value) => onMetricChange(value as Metric)}
          items={selectableMetrics.map((m) => ({
            id: m.value,
            label: m.label,
            supportingText: `(${m.description})`,
          }))}
          placeholder="Select Data"
          className="w-full"
        >
          {(item: SelectItemType) => (
            <SelectItem id={item.id} key={item.id} value={item}>
              {item.label}
            </SelectItem>
          )}
        </Select>
      </div>
      <Button
        iconLeading={<Trash02 className="h-5 w-5" />}
        color="tertiary"
        className="h-10 w-10 p-0 shrink-0"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onRemove();
        }}
      />
    </div>
  );
};

const defaultGenerateSuggestedName = ({
  layout,
  chartMetrics,
  tableMetrics,
  dateRange,
}: {
  layout: "chart" | "table" | "bar";
  chartMetrics: ("" | Metric)[];
  tableMetrics: ("" | Metric)[];
  dateRange?: { start: DateValue; end: DateValue } | null;
}) => {
  const validMetrics = layout === "chart" ? chartMetrics.filter((m) => m !== "") : tableMetrics.filter((m) => m !== "");

  if (validMetrics.length === 0) return "";

  // Convert metric IDs to labels using METRICS_MAP
  const metricLabels = validMetrics.map((m) => METRICS_MAP[m as Metric]?.label || m);

  // Use date range if provided, otherwise use today's date
  let dateStr: string;
  if (dateRange) {
    const startDateStr = dateRange.start.toDate(getLocalTimeZone()).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const endDateStr = dateRange.end.toDate(getLocalTimeZone()).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    dateStr = `${startDateStr} - ${endDateStr}`;
  } else {
    const today = new Date();
    dateStr = today.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  if (metricLabels.length <= 2) {
    return `${metricLabels.join(", ")} (${dateStr})`;
  }
  return `${metricLabels.slice(0, 2).join(", ")}, ${metricLabels.length - 2} more (${dateStr})`;
};

export const MetricsConfigurationForm = forwardRef<MetricsConfigurationFormHandle, MetricsConfigurationFormProps>(
  (
    {
      onSubmit,
      initialData,
      generateSuggestedName = defaultGenerateSuggestedName,
      detailsSectionLabel = "Report Details",
      detailsSectionSubtitle = "Name",
      resetOnInitialDataChange = false,
      onValidityChange,
      dateRangeSlot,
      dateRange,
      filteredMetrics,
    },
    ref,
  ) => {
    const [layout, setLayout] = useState<"chart" | "table" | "bar">(initialData?.layout ?? "chart");
    const [chartMetrics, setChartMetrics] = useState<(Metric | "")[]>(() => {
      if (initialData?.layout === "chart") {
        const metrics: (Metric | "")[] = [...initialData.metrics];
        while (metrics.length < 2) metrics.push("");
        return metrics;
      }
      // Pre-seed from table metrics so switching to chart immediately shows data
      if (initialData?.layout === "table" && initialData.metrics.length > 0) {
        const metrics: (Metric | "")[] = initialData.metrics.slice(0, 2) as (Metric | "")[];
        while (metrics.length < 2) metrics.push("");
        return metrics;
      }
      return ["", ""];
    });
    const [tableMetrics, setTableMetrics] = useState<(Metric | "")[]>(() => {
      if (initialData?.layout === "table") return initialData.metrics;
      // Pre-seed from chart metrics so switching to table immediately shows data
      if (initialData?.layout === "chart" && initialData.metrics.length > 0) {
        return [...initialData.metrics];
      }
      return [];
    });
    const [name, setName] = useState<string | null>(initialData?.name ?? null);
    const [suggestedName, setSuggestedName] = useState("");

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    const availableMetrics = useMemo(() => filteredMetrics ?? getMetricsArray(), [filteredMetrics]);

    // Reset form when initialData changes (for modal open/close behavior)
    useEffect(() => {
      if (resetOnInitialDataChange && initialData) {
        setLayout(initialData.layout);
        setName(initialData.name);
        if (initialData.layout === "chart") {
          const metrics: (Metric | "")[] = [...initialData.metrics];
          while (metrics.length < 2) metrics.push("");
          setChartMetrics(metrics);
          // Pre-seed table so switching views is non-destructive
          setTableMetrics([...initialData.metrics]);
        } else {
          setTableMetrics(initialData.metrics);
          // Pre-seed chart (first 2) so switching views is non-destructive
          const seeded: (Metric | "")[] = initialData.metrics.slice(0, 2) as (Metric | "")[];
          while (seeded.length < 2) seeded.push("");
          setChartMetrics(seeded);
        }
      } else if (resetOnInitialDataChange && !initialData) {
        // Reset to defaults when initialData is cleared
        setLayout("chart");
        setName(null);
        setChartMetrics(["", ""]);
        setTableMetrics([]);
      }
    }, [initialData, resetOnInitialDataChange]);

    // When switching layouts, carry metrics across if the target bucket is still empty
    const handleLayoutChange = (newLayout: "chart" | "table") => {
      setLayout(newLayout);
      if (newLayout === "chart") {
        const hasChartMetrics = chartMetrics.some((m) => m !== "");
        if (!hasChartMetrics && tableMetrics.length > 0) {
          const seeded: (Metric | "")[] = tableMetrics.filter((m) => m !== "").slice(0, 2) as (Metric | "")[];
          while (seeded.length < 2) seeded.push("");
          setChartMetrics(seeded);
        }
      } else {
        const hasTableMetrics = tableMetrics.length > 0;
        if (!hasTableMetrics) {
          const seeded = chartMetrics.filter((m) => m !== "") as Metric[];
          if (seeded.length > 0) setTableMetrics(seeded);
        }
      }
    };

    useEffect(() => {
      const generated = generateSuggestedName({ layout, chartMetrics, tableMetrics, dateRange });
      setSuggestedName(generated);
    }, [layout, chartMetrics, tableMetrics, dateRange, generateSuggestedName]);

    // Notify parent when form validity changes
    // biome-ignore lint/correctness/useExhaustiveDependencies: FP
    useEffect(() => {
      onValidityChange?.(isFormValid());
    }, [layout, chartMetrics, tableMetrics, name, suggestedName, onValidityChange]);

    const removeTableMetric = (index: number) => {
      setTableMetrics(tableMetrics.filter((_, i) => i !== index));
    };

    const updateTableMetric = (index: number, value: Metric) => {
      const metrics = [...tableMetrics];
      metrics[index] = value;
      setTableMetrics(metrics);
    };

    const updateChartMetric = (index: number, value: Metric) => {
      const metrics: (Metric | "")[] = [...chartMetrics];
      metrics[index] = value;
      setChartMetrics(metrics);
    };

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;
      const activeId = active.id as Metric;
      const overId = over.id as Metric;
      if (activeId === overId) return;

      if (layout === "chart") {
        const activeIndex = chartMetrics.indexOf(activeId);
        const overIndex = chartMetrics.indexOf(overId);
        if (activeIndex !== -1 && overIndex !== -1) {
          setChartMetrics((metrics) => arrayMove(metrics, activeIndex, overIndex));
        }
      } else {
        const activeIndex = tableMetrics.indexOf(activeId);
        const overIndex = tableMetrics.indexOf(overId);
        if (activeIndex !== -1 && overIndex !== -1) {
          setTableMetrics((metrics) => arrayMove(metrics, activeIndex, overIndex));
        }
      }
    };

    const isFormValid = () => {
      const derivedName = name !== null ? name.trim() : suggestedName.trim();
      if (!derivedName) return false;
      if (layout === "chart") {
        return chartMetrics.some((metric) => metric !== "");
      }
      return tableMetrics.some((metric) => metric !== "");
    };

    const handleSubmit = async (event?: React.FormEvent) => {
      if (event) {
        event.preventDefault();
      }
      if (!isFormValid()) return;

      const metrics =
        layout === "chart"
          ? chartMetrics.filter((metric) => metric !== "")
          : [...tableMetrics.filter((metric) => metric !== "").filter(Boolean)];
      const payload: MetricsFormData = {
        name: (name !== null ? name : suggestedName).trim(),
        layout,
        metrics,
      };

      const result = await onSubmit(payload);
      return result;
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        return await handleSubmit();
      },
      get isValid() {
        return isFormValid();
      },
    }));

    return (
      <form onSubmit={handleSubmit} className="w-full">
        <div className="space-y-6">
          <RadioGroup
            value={layout}
            onChange={(value: string) => handleLayoutChange(value as "chart" | "table")}
            className="grid grid-cols-2 gap-4"
          >
            <button type="button" className="relative cursor-pointer" onClick={() => handleLayoutChange("chart")}>
              <div className="relative flex flex-col items-center justify-center rounded-lg transition-colors">
                <div className="mb-2" style={{ width: "100%", height: "100%" }}>
                  <ChartFormatIcon className="w-max h-max" isSelected={layout === "chart"} />
                </div>
                <div className="flex items-center justify-between w-full">
                  <span className="text-sm font-medium">Line Chart</span>
                  <RadioButton value="chart" className="pointer-events-none" />
                </div>
              </div>
            </button>
            <button type="button" className="relative cursor-pointer" onClick={() => handleLayoutChange("table")}>
              <div className="relative flex flex-col items-center justify-center rounded-lg transition-colors">
                <div className="mb-2" style={{ width: "100%", height: "100%" }}>
                  <TableFormatIcon className="w-max h-max" isSelected={layout === "table"} />
                </div>
                <div className="flex items-center justify-between w-full">
                  <span className="text-sm font-medium">Table</span>
                  <RadioButton value="table" className="pointer-events-none" />
                </div>
              </div>
            </button>
          </RadioGroup>

          {layout === "chart" ? (
            <div className="flex gap-17.5 pt-6 border-t border-secondary">
              <div className="flex flex-col gap-1 w-[20%] shrink-0">
                <div className="flex gap-1.5 items-center">
                  <LineChartUp02 className="size-5 text-tertiary" />
                  <span className="text-sm font-medium text-secondary">Data to Show</span>
                </div>
                <span className="text-xs text-quaternary">What should be plotted inside the chart?</span>
              </div>

              <div className="flex flex-col gap-3 flex-1 min-w-0">
                {chartMetrics.map((metric, index) => (
                  <div key={index} className="flex flex-col gap-1.5">
                    <Label className="text-sm font-medium">Line {index + 1}</Label>
                    <Select
                      selectedKey={metric}
                      onSelectionChange={(value) => updateChartMetric(index, value as Metric)}
                      items={availableMetrics.map((metricOption) => ({
                        id: metricOption.value,
                        label: metricOption.label,
                      }))}
                      placeholder="Select Data"
                      className="w-full"
                    >
                      {(item: SelectItemType) => (
                        <SelectItem id={item.id} key={item.id} value={item}>
                          {item.label}
                        </SelectItem>
                      )}
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex gap-17.5 pt-6 border-t border-secondary">
              <div className="flex flex-col gap-1 w-[20%] shrink-0">
                <div className="flex gap-1.5 items-center">
                  <BarChartSquare02 className="size-5 text-tertiary" />
                  <span className="text-sm font-medium text-secondary">Metrics</span>
                </div>
                <span className="text-xs text-quaternary">Which metrics should be shown in the table</span>
              </div>

              <div className="flex flex-col gap-2 flex-1 min-w-0">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={tableMetrics.filter((m) => m !== "")} strategy={verticalListSortingStrategy}>
                    {tableMetrics
                      .filter((m) => m !== "")
                      .map((metric) => (
                        <SortableTableMetric
                          key={metric}
                          id={metric}
                          metric={metric}
                          onRemove={() => {
                            const actualIndex = tableMetrics.indexOf(metric);
                            removeTableMetric(actualIndex);
                          }}
                          onMetricChange={(newMetric) => {
                            const actualIndex = tableMetrics.indexOf(metric);
                            updateTableMetric(actualIndex, newMetric);
                          }}
                          availableMetrics={availableMetrics}
                          selectedMetrics={tableMetrics}
                        />
                      ))}
                  </SortableContext>
                </DndContext>

                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex-1 min-w-0">
                    <Select
                      selectedKey=""
                      onSelectionChange={(value) => {
                        if (value) {
                          setTableMetrics([...tableMetrics.filter((m) => m !== ""), value as Metric]);
                        }
                      }}
                      items={availableMetrics
                        .filter((m) => !tableMetrics.includes(m.value as Metric | ""))
                        .map((metricOption) => ({
                          id: metricOption.value,
                          label: metricOption.label,
                          supportingText: `(${metricOption.description})`,
                        }))}
                      placeholder="Choose another metric to show"
                      className="w-full"
                      isDisabled={
                        availableMetrics.filter((m) => !tableMetrics.includes(m.value as Metric | "")).length === 0
                      }
                    >
                      {(item: SelectItemType) => (
                        <SelectItem id={item.id} key={item.id} value={item}>
                          {item.label}
                        </SelectItem>
                      )}
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Date Range Slot - rendered above report name with divider */}
          {dateRangeSlot && <div className="pt-6 border-t border-secondary">{dateRangeSlot}</div>}

          {/* Report Details - Two Column Layout 20%/80% with divider */}
          <div className="flex gap-17.5 pt-6 border-t border-secondary">
            <div className="flex flex-col gap-1 w-[20%] shrink-0">
              <div className="flex gap-1.5 items-center">
                <Type02 className="size-5 text-tertiary" />
                <span className="text-sm font-medium text-secondary">{detailsSectionLabel}</span>
              </div>
              <span className="text-xs text-quaternary">{detailsSectionSubtitle}</span>
            </div>

            <div className="flex flex-col gap-2 flex-1 min-w-0">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="metrics-form-name">Name</Label>
                <Input
                  id="metrics-form-name"
                  value={name !== null ? name : suggestedName}
                  onChange={(value) => setName(value)}
                  className={`w-full ${name === null && suggestedName ? "placeholder:text-gray-400 placeholder:italic" : ""}`}
                  isRequired
                  maxLength={40}
                />
                <span className="text-sm text-tertiary">Maximum 40 Characters</span>
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  },
);

MetricsConfigurationForm.displayName = "MetricsConfigurationForm";
